import { useCallback, useMemo } from 'react';
import {
  Actions,
  ControlProps,
  CoreActions,
  JsonFormsCellRendererRegistryEntry,
  JsonFormsRendererRegistryEntry,
  Resolve,
  composePaths,
  isControl,
  rankWith,
  resolveSchema,
} from '@jsonforms/core';
import { JsonFormsContext, JsonFormsDispatch, useJsonForms, withJsonFormsControlProps } from '@jsonforms/react';
import { DelegatedControlContext, delegateTo } from '@/components/forms/controlMarks';
import { Box, IconButton, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';

/**
 * A formula's value the user may type over (`computed: { mode: default }`, Computed-Fields.md §5.4), marked by the
 * generator with `x-computed` on the field's schema.
 *
 * The field's own control — date picker, number input, whatever the next renderer is — is delegated to, with this
 * renderer left out of the set. What this adds:
 *  - an edit the user makes in that control also sets the field's override flag (`{field}IsOverridden`), so the
 *    save keeps the typed value instead of the formula's; the formula's own preview reaches the control through
 *    the form's data, never as an edit, so it never sets the flag, and neither does a commit of an unchanged value;
 *  - a typed value is shown in italics, with a small "reset to calculated" button at the right end of the label
 *    row — the same marker the inline grid's cell has. Reset clears the flag; the form's preview then puts the
 *    formula's value back, and the save recomputes it. A calculated value carries no marker, so the field is as
 *    tall as any other wherever it sits in the layout. The button sits on the label row, not inside the input,
 *    because the input belongs to whichever renderer the field delegates to;
 *  - while the record is in a state the field freezes in, the value is kept whatever the flag says (freeze wins,
 *    §5.5), so the button is disabled and says why rather than offering a click that changes nothing; on a
 *    control the user may not edit it only marks the value.
 */

/** The `x-computed` marker the generator writes on a mode: default field's schema. */
interface ComputedMarker {
  mode: 'default';
  overrideFlag: string;
  freezeInStates?: string[];
  stateField?: string;
}

function markerOf(fieldSchema: unknown): ComputedMarker | undefined {
  const marker = (fieldSchema as { 'x-computed'?: ComputedMarker } | undefined)?.['x-computed'];
  return marker?.mode === 'default' && typeof marker.overrideFlag === 'string' ? marker : undefined;
}

/**
 * Whether `action` changes the value at `path`. A control may commit the value it shows without the user changing
 * it — the date control does on every Tab, Enter and blur — and leaving a field is not typing over its formula.
 */
function changesValueAt(action: CoreActions, path: string, data: unknown): boolean {
  if (action.type !== Actions.UPDATE_DATA || action.path !== path) return false;
  const before = Resolve.data(data, path);
  return JSON.stringify(action.updater(before)) !== JSON.stringify(before);
}

export const computedDefaultTester = rankWith(
  30, // above every control renderer the field could otherwise get (lookups 10–15, gates 25): it delegates to them
  (uischema, schema, context) => {
    if (!isControl(uischema) || !schema) return false;
    const rootSchema = (context as { rootSchema?: typeof schema } | undefined)?.rootSchema ?? schema;
    try {
      return markerOf(resolveSchema(rootSchema, uischema.scope, rootSchema)) !== undefined;
    } catch {
      return false;
    }
  },
);

interface ComputedDefaultProps extends ControlProps {
  renderers?: JsonFormsRendererRegistryEntry[];
  cells?: JsonFormsCellRendererRegistryEntry[];
}

export const ComputedDefaultComponent = (props: ComputedDefaultProps) => {
  const { uischema, schema, rootSchema, path, visible, enabled, renderers, cells } = props;
  const ctx = useJsonForms();
  const { t } = useTranslation();
  const marker = markerOf(schema)!;

  // The control's path is its own ("dueDate"); its siblings — the flag, the state — live under the parent.
  const parentPath = path.includes('.') ? path.slice(0, path.lastIndexOf('.')) : '';
  const flagPath = composePaths(parentPath, marker.overrideFlag);
  const formData = ctx.core?.data;
  const overridden = Resolve.data(formData, flagPath) === true;
  const state = marker.stateField ? Resolve.data(formData, composePaths(parentPath, marker.stateField)) : undefined;
  const frozen = typeof state === 'string' && (marker.freezeInStates ?? []).includes(state);

  // An edit of this field's value, made in the delegated control, is the user typing over the formula.
  const { dispatch: formDispatch } = ctx;
  const dispatch = useCallback(
    (action: CoreActions) => {
      const typedOver = changesValueAt(action, path, formData);
      formDispatch?.(action);
      if (typedOver) {
        formDispatch?.(Actions.update(flagPath, () => true));
      }
    },
    [formDispatch, path, flagPath, formData],
  );
  const context = useMemo(() => ({ ...ctx, dispatch }), [ctx, dispatch]);
  const delegated = delegateTo(renderers ?? ctx.renderers ?? [], computedDefaultTester);

  if (!visible) return null;

  const typedIn = t('computed.typedIn', { defaultValue: 'Typed in' });
  const reset = t('computed.reset', { defaultValue: 'Reset to calculated' });
  const why = frozen
    ? `${typedIn} – ${t('computed.frozen', { defaultValue: 'kept while the record is {{state}}', state })}`
    : enabled ? `${typedIn} – ${reset}` : typedIn;

  return (
    <Box
      data-testid={`computed-default-${marker.overrideFlag}`}
      data-overridden={overridden}
      sx={{ position: 'relative', ...(overridden ? { '& input': { fontStyle: 'italic' } } : {}) }}
    >
      <JsonFormsContext.Provider value={context}>
        {/* JsonFormsDispatch composes the control's path from the PARENT path and the scope, against the root schema.
            The hand-off is the same control: a form's control marks were put on this renderer already. */}
        <DelegatedControlContext.Provider value={path}>
          <JsonFormsDispatch
            uischema={uischema}
            schema={rootSchema}
            path={parentPath}
            enabled={enabled}
            visible={visible}
            renderers={delegated}
            cells={cells ?? ctx.cells}
          />
        </DelegatedControlContext.Provider>
      </JsonFormsContext.Provider>
      {overridden && (
        <Tooltip title={why}>
          <Box component="span" sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1, lineHeight: 0 }}>
            <IconButton
              size="small"
              aria-label={reset}
              disabled={frozen || !enabled}
              onClick={() => formDispatch?.(Actions.update(flagPath, () => false))}
              sx={{ p: 0.25, color: 'warning.main' }}
            >
              <RestartAltIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export const ComputedDefaultRenderer = withJsonFormsControlProps(ComputedDefaultComponent);

export const computedDefaultRendererEntry: JsonFormsRendererRegistryEntry = {
  tester: computedDefaultTester,
  renderer: ComputedDefaultRenderer,
};
